/**
 * Availability Urgency Utilities
 *
 * Task: T-P4-D5-F3-2
 *
 * Provides utility functions for calculating and displaying capacity urgency levels.
 * Used by CWR event surfaces to show spots remaining with appropriate styling.
 *
 * Urgency thresholds:
 * - Critical: <= 5 spots OR <= 25% capacity remaining
 * - Normal: More than 5 spots AND more than 25% capacity remaining
 */

/**
 * Urgency level for capacity display.
 * - 'critical': Event is running low on spots (red/warning styling)
 * - 'normal': Event has plenty of spots (standard styling)
 */
export type UrgencyLevel = 'critical' | 'normal';

/**
 * Determine the urgency level based on spots remaining and total capacity.
 *
 * Critical threshold: <= 5 spots OR <= 25% capacity remaining
 *
 * @param spotsRemaining - Number of available spots
 * @param capacity - Total capacity of the event
 * @returns 'critical' or 'normal'
 *
 * @example
 * getUrgencyLevel(3, 20);  // 'critical' (3 <= 5)
 * getUrgencyLevel(10, 20); // 'normal' (10 > 5 AND 50% > 25%)
 * getUrgencyLevel(5, 100); // 'critical' (5% <= 25%)
 * getUrgencyLevel(26, 100); // 'normal' (26% > 25%)
 */
export function getUrgencyLevel(spotsRemaining: number, capacity: number): UrgencyLevel {
  // Absolute threshold: <= 5 spots is always critical
  if (spotsRemaining <= 5) {
    return 'critical';
  }

  // Relative threshold: <= 25% capacity is critical
  // Guard against divide-by-zero (treat 0 capacity as critical)
  if (capacity <= 0 || spotsRemaining / capacity <= 0.25) {
    return 'critical';
  }

  return 'normal';
}

/**
 * Check if the event is fully booked (no spots remaining).
 *
 * @param spotsRemaining - Number of available spots
 * @returns true if no spots available
 */
export function isFullyBooked(spotsRemaining: number): boolean {
  return spotsRemaining <= 0;
}

/**
 * Format spots remaining for display.
 *
 * @param spotsRemaining - Number of available spots
 * @returns Formatted string (e.g., "3 spots left", "1 spot left", "Sold out")
 */
export function formatSpotsRemaining(spotsRemaining: number): string {
  if (spotsRemaining <= 0) {
    return 'Sold out';
  }
  if (spotsRemaining === 1) {
    return '1 spot left';
  }
  return `${spotsRemaining} spots left`;
}
